import { ICredentialErrors } from "../../user/types/icredential-errors.interface";
import { ERROR_MESSAGE } from "../../../constants/errors";
import { AuthToken } from "../../user/types/auth-token.interface";
import backend, { clearAuthorizationHeader } from "../../../services/axios.functions";
import { UserData, UserEmailData } from "../../../pages/login/types/user-data.interface";
import { from, Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import { Nullable } from "../../../types/common";
import jwtDecode from "jwt-decode";
import { UserCredentials } from "../types/user-credentials.interface";

export const ACCESS_TOKEN_NAME = "asbn-access-token";
export const REFRESH_TOKEN_NAME = "asbn-refresh-token";

export function login(userData: UserData): Observable<AuthToken> {
  return from(backend.post<AuthToken>("/token/", userData)).pipe(
    map((response) => response.data),
    tap(setTokenPair)
  );
}

export function pingBackend(): Observable<number> {
  return from(backend.get("")).pipe(
    map((response) => {
      if (response.status !== 200) {
        throwNotAuthenticated();
      }
      return response.status;
    })
  );
}

export function authenticate(): Observable<UserCredentials> {
  // @ts-ignore
  return pingBackend().pipe(
    map(() => {
      const credentials = getCredentials();
      if (!credentials) {
        throwNotAuthenticated();
      }
      return credentials;
    })
  );
}

export function sendResetPasswordRequest(userData: UserEmailData): Observable<number> {
  return from(backend.post("/users/reset-password-request/", userData)).pipe(
    map((response) => {
      if (response.status !== 200) {
        throw new Error("Something went wrong, please try again");
      }
      return 200;
    })
  );
}

export function throwNotAuthenticated() {
  throw new Error("Not authenticated");
}

export function destroySession() {
  deleteTokenPair();
  clearAuthorizationHeader();
}

export const validateCredentialInputs = (email: string, password: string): ICredentialErrors => {
  let emailErrorMessage;
  let passwordErrorMessage;

  if (!email || email === "") {
    emailErrorMessage = ERROR_MESSAGE.VACANT_FIELD;
  } else if (!validateEmail(email)) {
    emailErrorMessage = ERROR_MESSAGE.INVALID_EMAIL;
  }

  if (!password || password === "") {
    passwordErrorMessage = ERROR_MESSAGE.VACANT_FIELD;
  }

  return {
    emailError: emailErrorMessage,
    passwordError: passwordErrorMessage,
  };
};

export const validateEmailCredentialInput = (email: string): ICredentialErrors => {
  let emailErrorMessage;

  if (!email || email === "") {
    emailErrorMessage = ERROR_MESSAGE.VACANT_FIELD;
  } else if (!validateEmail(email)) {
    emailErrorMessage = ERROR_MESSAGE.INVALID_EMAIL;
  }

  return {
    emailError: emailErrorMessage,
  };
};

export function hasError(error: ICredentialErrors): boolean {
  return Boolean(error.emailError) || Boolean(error.passwordError);
}

export const validateEmail = (email: string): boolean => {
  const re = /\S+@\S+\.\S+/;
  return re.test(email);
};

export const setTokenPair = ({ access, refresh }: AuthToken) => {
  access && window && window.localStorage.setItem(ACCESS_TOKEN_NAME, access);
  refresh && window && window.localStorage.setItem(REFRESH_TOKEN_NAME, refresh);
};

export const deleteTokenPair = () => {
  window && window.localStorage.removeItem(ACCESS_TOKEN_NAME);
  window && window.localStorage.removeItem(REFRESH_TOKEN_NAME);
};

export const getTokenPair = () => ({
  accessToken: window && window.localStorage.getItem(ACCESS_TOKEN_NAME),
  refreshToken: window && window.localStorage.getItem(REFRESH_TOKEN_NAME),
});

export const getCredentials = (): Nullable<UserCredentials> => {
  const { accessToken } = getTokenPair();

  if (typeof accessToken !== "string") {
    return null;
  }

  try {
    const { user_id: userId } = jwtDecode<{ user_id: number }>(accessToken);
    return { userId };
  } catch (err) {
    console.log(`Failed to extract credentials with error: ${err}`);
    return null;
  }
};
